import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, ArrowLeft, Plus } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLogout } from "@/hooks/useLogout";
import { useUserAvatar } from "@/hooks/useUserAvatar";
import { useSidebar } from "@/contexts/SidebarContext";
import { cn } from "@/components/utils/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { NotificationBell } from "./NotificationBell";
import { UserAvatarMenu } from "./UserAvatarMenu";

interface HeaderProps {
  pageTitle?: string;
  pageDescription?: string;
  showBackButton?: boolean;
}

const Header = ({
  pageTitle = "Dashboard",
  pageDescription = "Welcome to your dashboard",
  showBackButton = true,
}: HeaderProps) => {
  const { user, userRole } = useAuth();
  const { logout, isLoggingOut } = useLogout();
  const { displayName } = useUserAvatar();
  const navigate = useNavigate();
  const { toggleMobileSidebar, isSidebarCollapsed } = useSidebar();
  const { t } = useLanguage();

  const handleLogout = () => {
    logout("/login");
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <header
      className={cn(
        "fixed top-0 z-50 bg-white border-b border-gray-100 shadow-sm transition-all duration-300 ease-in-out",
        // Mobile: full width, Desktop: positioned relative to sidebar
        "left-0 right-0 md:left-auto md:right-0",
        // Desktop positioning based on sidebar state
        isSidebarCollapsed ? "md:left-[70px]" : "md:left-[208px]",
      )}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20 md:h-[100px]">
          {/* Mobile Title */}
          <div className="md:hidden">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {pageTitle}
              </h1>
              <p className="text-sm text-gray-600">{pageDescription}</p>
            </div>
          </div>

          {/* Desktop Page Title and Description */}
          <div className="hidden md:block">
            <h1 className="text-2xl text-gray-900">
              {/* {showBackButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="h-8 w-8 p-0 hover:bg-gray-100 mr-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )} */}
              {pageTitle}
            </h1>
            <p className="text-sm text-gray-600 mt-1">{pageDescription}</p>
          </div>

          {/* User Menu Section */}
          <div className="flex items-center gap-2 md:gap-4">
            {user && (
              <>
                {/* New Trip Button - Only on Desktop */}
                <div className="relative hidden md:block">
                  <Link
                    to={
                      userRole === "travel_designer"
                        ? `/request-trip?td=${user.id}`
                        : "/request-trip"
                    }
                  >
                    {" "}
                    <Button variant="default" size="lg" className="h-8 px-3">
                      <Plus className="h-4 w-4 mr-1" />
                      {t("navigation.newTrip")}
                    </Button>
                  </Link>
                </div>

                <NotificationBell />

                {/* User Avatar & Dropdown */}
                <UserAvatarMenu
                  displayName={displayName}
                  email={user.email ?? ''}
                  onLogout={handleLogout}
                  isLoggingOut={isLoggingOut}
                />

                {/* Mobile Menu Button - Now on the right */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleMobileSidebar}
                  className="h-10 w-10 md:h-8 md:w-8 p-0 hover:bg-gray-100 md:hidden"
                >
                  <Menu className="h-5 w-5 md:h-4 md:w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
